import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, map, share } from 'rxjs/operators';
import {
  WebSocketMessage,
  HeartbeatMessage,
  PongMessage,
  AckMessage,
  UserEventMessage,
  ConversationEventMessage,
  MessageEventMessage,
  StateEventMessage,
  ServerMessage
} from '../models';

/**
 * Message routing and parsing service
 * Handles incoming message classification and routing to specific streams
 */
@Injectable({
  providedIn: 'root'
})
export class MessageRouterService implements OnDestroy {
  private readonly heartbeatSubject$ = new Subject<HeartbeatMessage>();
  private readonly pongSubject$ = new Subject<PongMessage>();
  private readonly ackSubject$ = new Subject<AckMessage>();
  private readonly userEventSubject$ = new Subject<UserEventMessage>();
  private readonly conversationEventSubject$ = new Subject<ConversationEventMessage>();
  private readonly messageEventSubject$ = new Subject<MessageEventMessage>();
  private readonly stateEventSubject$ = new Subject<StateEventMessage>();

  // Public specialized message streams
  readonly heartbeats$ = this.heartbeatSubject$.asObservable();
  readonly pongs$ = this.pongSubject$.asObservable();
  readonly acks$ = this.ackSubject$.asObservable();
  readonly userEvents$ = this.userEventSubject$.asObservable();
  readonly conversationEvents$ = this.conversationEventSubject$.asObservable();
  readonly messageEvents$ = this.messageEventSubject$.asObservable();
  readonly stateEvents$ = this.stateEventSubject$.asObservable();

  /**
   * Process incoming WebSocket messages and route them to appropriate streams
   */
  routeMessage(message: WebSocketMessage): void {
    if (!message.data || typeof message.data !== 'object') {
      return;
    }

    const serverMessage = message.data as any;

    switch (serverMessage.event_type) {
      case 'heartbeat':
        if (this.isHeartbeatMessage(serverMessage)) {
          this.heartbeatSubject$.next(serverMessage);
        }
        break;

      case 'pong':
        if (this.isPongMessage(serverMessage)) {
          this.pongSubject$.next(serverMessage);
        }
        break;

      case 'ack':
        if (this.isAckMessage(serverMessage)) {
          this.ackSubject$.next(serverMessage);
        }
        break;

      case 'user_event':
        if (this.isUserEventMessage(serverMessage)) {
          this.userEventSubject$.next(serverMessage);
        }
        break;

      case 'conversation_event':
        if (this.isConversationEventMessage(serverMessage)) {
          this.conversationEventSubject$.next(serverMessage);
        }
        break;

      case 'message_event':
        if (this.isMessageEventMessage(serverMessage)) {
          this.messageEventSubject$.next(serverMessage);
        }
        break;

      case 'state_event':
        if (this.isStateEventMessage(serverMessage)) {
          this.stateEventSubject$.next(serverMessage);
        }
        break;
    }
  }

  /**
   * Create filtered message streams based on message type
   */
  createFilteredStream<T>(
    messages$: Observable<WebSocketMessage>,
    predicate: (message: WebSocketMessage) => boolean,
    mapper?: (message: WebSocketMessage) => T
  ): Observable<T> {
    return messages$.pipe(
      filter(predicate),
      map(mapper || ((msg) => msg.data as T)),
      share()
    );
  }

  /**
   * Get all server messages as a typed stream
   */
  getServerMessages(messages$: Observable<WebSocketMessage>): Observable<ServerMessage> {
    return messages$.pipe(
      filter(msg => this.isServerMessage(msg)),
      map(msg => msg.data as ServerMessage),
      share()
    );
  }

  // Type guard functions
  private isHeartbeatMessage(data: any): data is HeartbeatMessage {
    return data.event_type === 'heartbeat' &&
           typeof data.timestamp === 'string';
  }

  private isPongMessage(data: any): data is PongMessage {
    return data.event_type === 'pong' &&
           typeof data.connection_id === 'string' &&
           typeof data.timestamp === 'string';
  }

  private isAckMessage(data: any): data is AckMessage {
    return data.event_type === 'ack' &&
           typeof data.user_id === 'string' &&
           typeof data.timestamp === 'string';
  }

  private isUserEventMessage(data: any): data is UserEventMessage {
    return data.event_type === 'user_event' &&
           typeof data.event === 'string' &&
           typeof data.agent_id === 'string' &&
           typeof data.email === 'string' &&
           typeof data.ip_address === 'string' &&
           typeof data.reason === 'string' &&
           typeof data.timestamp === 'string';
  }

  private isConversationEventMessage(data: any): data is ConversationEventMessage {
    return data.event_type === 'conversation_event' &&
           typeof data.conversation_id === 'string' &&
           typeof data.key_prefix === 'string' &&
           typeof data.start_date === 'string' &&
           typeof data.client_id === 'string' &&
           typeof data.channel_id === 'string' &&
           typeof data.platform === 'string' &&
           typeof data.status === 'string' &&
           typeof data.campaign_id === 'string' &&
           typeof data.custom_variables === 'object' &&
           typeof data.timestamp === 'string';
  }

  private isMessageEventMessage(data: any): data is MessageEventMessage {
    return data.event_type === 'message_event' &&
           typeof data.message_id === 'string' &&
           typeof data.conversation_id === 'string' &&
           typeof data.type === 'string' &&
           typeof data.message_type === 'string' &&
           typeof data.content === 'string' &&
           typeof data.date_pending === 'string' &&
           typeof data.date_sent === 'string' &&
           typeof data.date_delivered === 'string' &&
           typeof data.status === 'string' &&
           typeof data.agent_id === 'string' &&
           typeof data.timestamp === 'string';
  }

  private isStateEventMessage(data: any): data is StateEventMessage {
    return data.event_type === 'state_event' &&
           typeof data.id === 'string' &&
           typeof data.email === 'string' &&
           typeof data.username === 'string' &&
           typeof data.version === 'string' &&
           typeof data.ip === 'string' &&
           typeof data.lastUpdate === 'string' &&
           typeof data.createdAt === 'string' &&
           typeof data.role === 'string' &&
           typeof data.status === 'string' &&
           Array.isArray(data.campaigns) &&
           typeof data.timestamp === 'string';
  }

  private isServerMessage(message: WebSocketMessage): boolean {
    if (!message.data || typeof message.data !== 'object') {
      return false;
    }

    const eventType = message.data.event_type;
    return eventType === 'heartbeat' || eventType === 'pong' || eventType === 'ack' ||
           eventType === 'user_event' || eventType === 'conversation_event' || eventType === 'message_event' ||
           eventType === 'state_event';
  }

  /**
   * Cleanup all subjects to prevent memory leaks
   */
  ngOnDestroy(): void {
    this.heartbeatSubject$.complete();
    this.pongSubject$.complete();
    this.ackSubject$.complete();
    this.userEventSubject$.complete();
    this.conversationEventSubject$.complete();
    this.messageEventSubject$.complete();
    this.stateEventSubject$.complete();
  }
}
